import { FC, useEffect, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { Button, TextArea } from '@carbon/react';
import { Close } from '@carbon/react/icons';
import { slotOverridesAtom } from 'pages/type-curves/atoms';
import { valuationTargetAtom } from './atoms';
import { useUnitBuilder } from './context';
import { useBuilderGeneration } from './use-builder-generation';
import style from './unit-builder-modal.module.scss';

/**
 * The edit rail's value/unvalue Notes section.
 *
 * Shown when a property is right-clicked in the gun barrel (via
 * `valuationTargetAtom`). The optimistic value/unvalue toggle already happened on
 * the right-click; this captures the reason and finalizes the toggle. Save
 * persists through the slot-editing PATCH, then restamps the gun barrel and the
 * slot table through the narrow valuation trigger — not the geometry fan-out,
 * which a flag change gives nothing to re-read; Cancel reverts.
 *
 * @return {JSX.Element | null} The Notes section, or null when nothing is targeted.
 */
export const UnitBuilderNotesSection: FC = () => {
  const [target, setTarget] = useAtom(valuationTargetAtom);
  const setSlotOverrides = useSetAtom(slotOverridesAtom);
  const { inventoryWorkspaceId } = useUnitBuilder();
  const { updateValuation, valuing } =
    useBuilderGeneration(inventoryWorkspaceId);
  const [notes, setNotes] = useState('');

  // Reseed the draft when a different property is targeted. `target.notes` only
  // changes alongside a new `slotId`, so this never clobbers an in-progress edit.
  useEffect(() => {
    setNotes(target?.notes ?? '');
  }, [target?.slotId, target?.notes]);

  if (!target) return null;

  const unvaluing = target.valued === false;
  // The model requires a note to unvalue; re-valuing needs none.
  const canSave = !unvaluing || notes.trim().length > 0;

  const handleCancel = () => {
    // Undo the optimistic toggle — drop the override back to the server value.
    setSlotOverrides((prev) => {
      const next = new Map(prev);
      next.delete(target.slotId);
      return next;
    });
    setTarget(null);
  };

  const handleSave = () => {
    if (!canSave || valuing) return;
    const trimmed = notes.trim() ? notes.trim() : null;

    // Hold the optimistic value on the chart, map and slots table while the
    // PATCH is in flight; the refreshed slots then carry it, and a failure
    // drops it back to what the server still says.
    setSlotOverrides((prev) => {
      const next = new Map(prev);
      next.set(target.slotId, { valued: target.valued, notes: trimmed });
      return next;
    });

    updateValuation({
      slotId: target.slotId,
      valued: target.valued,
      notes: trimmed,
      onSaved: () => setTarget(null),
      // Keep the section open so the reason can be corrected and retried —
      // NOTE_REQUIRED and a promoted workspace both land here.
      onFailed: () =>
        setSlotOverrides((prev) => {
          const next = new Map(prev);
          next.delete(target.slotId);
          return next;
        }),
    });
  };

  return (
    <div className={style.editSection}>
      <div className={style.editSectionHead}>
        <span className={style.editSectionLabel}>Notes</span>
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={Close}
          iconDescription="Close"
          tooltipPosition="left"
          onClick={handleCancel}
        />
      </div>
      <p className={style.railHint}>
        {unvaluing
          ? `State the reason to unvalue Property ${target.label} and click Save note.`
          : `Add a note for Property ${target.label}.`}
      </p>
      <TextArea
        id="unit-builder-property-note"
        labelText="Note"
        hideLabel
        placeholder="Add a note for this property…"
        rows={4}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
      <div className={style.notesActions}>
        <Button
          kind="secondary"
          size="sm"
          disabled={valuing}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          kind="primary"
          size="sm"
          disabled={!canSave || valuing}
          onClick={handleSave}
        >
          {valuing ? 'Saving...' : 'Save note'}
        </Button>
      </div>
    </div>
  );
};
